import {PI_CARDINAL} from '../pi/piPaths';
import type {PlushState} from './PaiPlush';
import {KnitBeanie, PlushFace, PlushFrame} from './plushKit';

/**
 * Maple: a rusty-orange red panda plush clutching an ace of hearts to its
 * chest — card-drawing probability. Cream ear tufts, muzzle, and eyebrow
 * dots; the red panda's dark tear marks; dark paws; and a ringed tail.
 */

const FUR = {light: '#E69556', mid: '#C9652F', dark: '#9A4620'};
const DARK = '#5E2E18';
const CREAM = '#FFF1E0';
const TEAR = '#8A3A18';
const TAIL_RING = '#8C3E1A';

/** Props for {@link MaplePlush}. */
export interface MaplePlushProps {
  /** Rendered width in px. */
  size: number;
  /** Expression to show. Omitted defaults to `idle`. */
  state?: PlushState;
  /** Puts Beanie's knit hat on it, in the given color. Omitted means no hat. */
  beanieColor?: string;
}

/**
 * Renders Maple (see module comment). Decorative (`aria-hidden`).
 *
 * @param props - See {@link MaplePlushProps}.
 * @return The mascot SVG.
 * @throws Never.
 */
export function MaplePlush({
  size,
  state = 'idle',
  beanieColor,
}: MaplePlushProps) {
  const hasHat = beanieColor !== undefined;
  // Ears drop to peek out under the cuff when hatted, like Ollie's.
  const ears = hasHat
    ? [
        {cx: 32, cy: 78, tilt: -40},
        {cx: 168, cy: 78, tilt: 40},
      ]
    : [
        {cx: 48, cy: 46, tilt: -20},
        {cx: 152, cy: 46, tilt: 20},
      ];

  return (
    <PlushFrame
      size={size}
      state={state}
      furStops={FUR}
      eyeLineFraction={98 / 220}
      thoughtAnchor={{x: 158, y: 44}}
    >
      {kit => (
        <>
          {/* Ringed tail curling up behind, lower right. */}
          <g filter={kit.fuzz}>
            <path
              d="M 150 180 C 186 184 198 150 186 120"
              stroke={kit.fur}
              strokeWidth={26}
              strokeLinecap="round"
              fill="none"
            />
          </g>
          <path
            d="M 172 178 l 8 -12 M 188 156 l 10 -2 M 190 132 l 10 4"
            stroke={TAIL_RING}
            strokeWidth={7}
            strokeLinecap="round"
          />
          <g filter={kit.fuzz}>
            {ears.map(({cx, cy, tilt}) => (
              <ellipse
                key={cx}
                cx={cx}
                cy={cy}
                rx={17}
                ry={21}
                fill={kit.fur}
                transform={`rotate(${tilt} ${cx} ${cy})`}
              />
            ))}
            <ellipse cx={76} cy={198} rx={18} ry={9} fill={DARK} />
            <ellipse cx={124} cy={198} rx={18} ry={9} fill={DARK} />
            <path
              d="M100 30 C150 30 176 72 176 122 C176 166 146 194 100 194 C54 194 24 166 24 122 C24 72 50 30 100 30 Z"
              fill={kit.fur}
            />
          </g>
          {ears.map(({cx, cy, tilt}) => (
            <ellipse
              key={cx}
              cx={cx}
              cy={cy + 3}
              rx={9}
              ry={12}
              fill={CREAM}
              transform={`rotate(${tilt} ${cx} ${cy})`}
            />
          ))}
          {hasHat && <KnitBeanie kit={kit} color={beanieColor} />}
          <g filter={kit.soft}>
            <ellipse cx={100} cy={116} rx={30} ry={20} fill={CREAM} />
            <circle cx={72} cy={80} r={6} fill={CREAM} />
            <circle cx={128} cy={80} r={6} fill={CREAM} />
          </g>
          <path
            d="M 70 112 Q 68 120 64 126 M 130 112 Q 132 120 136 126"
            stroke={TEAR}
            strokeWidth={4}
            strokeLinecap="round"
            fill="none"
            opacity={0.35}
          />
          <PlushFace kit={kit} x={100} y={98} scale={0.95} />
          <path
            d="M 94 105 Q 100 103 106 105 Q 104 111 100 111 Q 96 111 94 105 Z"
            fill="#2A1612"
          />
          {/* Ace of hearts, held to the chest. */}
          <g transform="rotate(6 100 160)">
            <rect
              x={77}
              y={132}
              width={46}
              height={58}
              rx={6}
              fill="#FFFDF8"
              stroke="#E4D9C6"
              strokeWidth={1.5}
            />
            <path
              d="M 100 170 C 88 160 88 150 95 150 C 98 150 100 153 100 155 C 100 153 102 150 105 150 C 112 150 112 160 100 170 Z"
              fill={PI_CARDINAL}
            />
            <text
              x={86}
              y={147}
              fontSize={11}
              fontWeight={700}
              fontFamily="Jost, system-ui, sans-serif"
              fill={PI_CARDINAL}
            >
              A
            </text>
          </g>
          <g filter={kit.fuzz}>
            <ellipse cx={80} cy={164} rx={11} ry={13} fill={DARK} />
            <ellipse cx={120} cy={160} rx={11} ry={13} fill={DARK} />
          </g>
        </>
      )}
    </PlushFrame>
  );
}
